import type { Dispatch, FormEvent } from "react";
import { PHASE } from "../../../common/constants.ts";
import { helpers } from "../../util/helpers.ts";
import { showNotification } from "../../util/showNotification.ts";
import { toWorker } from "../../util/toWorker.ts";
import type { Phase } from "../../../common/types.ts";
import type { Action } from "./index.tsx";

type Props = {
	dispatch: Dispatch<Action>;
	phase: Phase;
	saving: boolean;
};

const AddRemove = ({ dispatch, phase, saving }: Props) => {
	const validPhases: Phase[] = [
		PHASE.PRESEASON,
		PHASE.AFTER_DRAFT,
		PHASE.RESIGN_PLAYERS,
		PHASE.FREE_AGENCY,
	];
	const phaseDisabled = !validPhases.includes(phase);

	const addTeam = async (e: FormEvent) => {
		e.preventDefault();
		dispatch({
			type: "startSaving",
		});
		const t = await toWorker("main", "addTeam", undefined);
		dispatch({
			type: "addTeam",
			team: t,
		});
		showNotification({
			type: "success",
			text: "Added new team.",
		});
		dispatch({
			type: "doneSaving",
		});
	};

	return (
		<>
			{phaseDisabled ? (
				<p className="text-danger">
					You can only add or remove teams during the preseason, after draft,
					re-signing, or free agency game phases.
				</p>
			) : null}
			<div className="row">
				<div className="col-sm">
					<h2>Add Team</h2>
					<p>
						After you add a team here, it will become available to edit in the
						form below. Created teams start with no players. You can use God
						Mode to set up the roster, or start simulating and the AI will sign
						free agents.
					</p>
					<p>
						An{" "}
						<a href={helpers.leagueUrl(["expansion_draft"])}>expansion draft</a>{" "}
						is generally a better way to add a team.
					</p>

					<form onSubmit={addTeam}>
						<button
							type="submit"
							className="btn btn-primary mb-3"
							disabled={phaseDisabled || saving}
						>
							Add Team
						</button>
					</form>
				</div>
				<div className="col-sm">
					<h2>Remove Team</h2>

					<p>
						You can disable a team by changing its <b>Status</b> from "Active"
						to "Inactive" in the table below. This can be done at any time
						except during the regular season, playoffs, or draft.
					</p>
					<p>
						When a team is disabled, all its players become free agents. Because
						of this, the best time to disable a team is right after the playoffs
						finish. Then the draft and free agency can proceed like normal, just
						with fewer teams.
					</p>
					<p>
						Team history is preserved for disabled teams, and disabled teams can
						be re-enabled in the future, either here or in an{" "}
						<a href={helpers.leagueUrl(["expansion_draft"])}>expansion draft</a>
						.
					</p>
				</div>
			</div>
		</>
	);
};

export default AddRemove;
